import {
  doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs,
  writeBatch, serverTimestamp, increment, limit
} from 'firebase/firestore';
import { getDbSafe, getAuthSafe } from './firebaseConfig';

export interface InviteRewardDoc {
  invitedUsernames: string[];
  completed: boolean;
  rewarded: boolean;
  createdAt: any;
}

export type ValidationResult =
  | { ok: true }
  | { ok: false; error: string };

const REWARD_GOLD = 500;
const MAX_INVITES = 5;

/** Load or create the invite reward doc for the current user. */
export async function getInviteReward(uid: string): Promise<InviteRewardDoc | null> {
  const db = await getDbSafe();
  const ref = doc(db, 'inviteRewards', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data() as InviteRewardDoc;
}

/** Check whether the current user has already completed (and been rewarded for) the invite campaign. */
export async function isInviteCompleted(uid: string): Promise<boolean> {
  const reward = await getInviteReward(uid);
  return !!(reward?.completed && reward?.rewarded);
}

/**
 * Validate a username for the invite reward.
 * Returns { ok: true } if valid, or { ok: false, error: string } if not.
 */
export async function validateInviteUsername(
  username: string,
  inviterUid: string,
  inviterCreatedAt: any,
  alreadyAdded: string[]
): Promise<ValidationResult> {
  const db = await getDbSafe();
  const clean = username.startsWith('@') ? username : `@${username}`;

  // Check not self
  const auth = await getAuthSafe();
  const currentUser = auth.currentUser;
  if (!currentUser) return { ok: false, error: 'Not logged in.' };

  // Rule: must be a real user
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('username', '==', clean), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return { ok: false, error: 'User not found.' };

  const invitedUser = snap.docs[0].data();
  const invitedUid = snap.docs[0].id;

  // Rule: cannot invite self
  if (invitedUid === inviterUid) return { ok: false, error: 'You cannot invite yourself.' };

  function parseTimestamp(val: any): number {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    if (typeof val.toMillis === 'function') return val.toMillis();
    if (val.seconds !== undefined) return val.seconds * 1000;
    if (val._seconds !== undefined) return val._seconds * 1000;
    if (val instanceof Date) return val.getTime();
    const parsed = new Date(val).getTime();
    if (!isNaN(parsed)) return parsed;
    return 0;
  }

  // Rule: invited user must be newer
  const inviterTime = parseTimestamp(inviterCreatedAt);
  const invitedTime = parseTimestamp(invitedUser.createdAt);

  if (invitedTime > 0 && inviterTime > 0 && invitedTime <= inviterTime) {
    return { ok: false, error: 'This user joined before you.' };
  }

  // Rule: not already added in this session
  const cleanLower = clean.toLowerCase();
  if (alreadyAdded.map(u => u.toLowerCase()).includes(cleanLower)) {
    return { ok: false, error: 'You already added this username.' };
  }

  // Rule: global anti-cheat — username cannot be used by ANYONE
  const globalRef = doc(db, 'inviteUsedUsernames', cleanLower.replace('@', ''));
  const globalSnap = await getDoc(globalRef);
  if (globalSnap.exists()) {
    return { ok: false, error: 'This username has already been used for a reward.' };
  }

  return { ok: true };
}

/**
 * Save a new validated invite username and, if all 5 are filled, grant the reward.
 * Uses batched writes for atomicity.
 */
export async function saveInviteUsername(
  inviterUid: string,
  username: string,
  currentUsernames: string[]
): Promise<{ rewarded: boolean }> {
  const db = await getDbSafe();
  const clean = (username.startsWith('@') ? username : `@${username}`).toLowerCase();
  const cleanKey = clean.replace('@', '');

  const newList = [...currentUsernames, clean];
  const isComplete = newList.length >= MAX_INVITES;

  const batch = writeBatch(db);

  // 1. Update or create the inviteRewards doc
  const rewardRef = doc(db, 'inviteRewards', inviterUid);
  const rewardSnap = await getDoc(rewardRef);

  if (rewardSnap.exists()) {
    batch.update(rewardRef, {
      invitedUsernames: newList,
      completed: isComplete,
      rewarded: isComplete,
    });
  } else {
    batch.set(rewardRef, {
      invitedUsernames: newList,
      completed: isComplete,
      rewarded: isComplete,
      createdAt: serverTimestamp(),
    });
  }

  // 2. Mark the username as globally used
  const globalRef = doc(db, 'inviteUsedUsernames', cleanKey);
  batch.set(globalRef, {
    usedBy: inviterUid,
    usedAt: serverTimestamp(),
  });

  // 3. If complete, grant gold
  if (isComplete) {
    const userRef = doc(db, 'users', inviterUid);
    batch.update(userRef, {
      gold: increment(REWARD_GOLD),
      goldBalance: increment(REWARD_GOLD),
    });
  }

  await batch.commit();
  return { rewarded: isComplete };
}
